import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const BranchCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [branchname, setbranchname] = useState("");
  const [contactperson, setcontactperson] = useState("");
  const [phoneno, setphoneno] = useState("");
  const [gstno, setgstno] = useState("");
  const [email, setemail] = useState("");
  const [address, setaddress] = useState("");
  const [country, setcountry] = useState(null);
  const [countryOptions, setcountryOptions] = useState([]);
  const [state, setstate] = useState(null);
  const [stateOptions, setstateOptions] = useState([]);
  const [city, setcity] = useState(null);
  const [cityOptions, setcityOptions] = useState([]);
  const [zipcode, setzipcode] = useState("");
  const [accountno, setaccountno] = useState("");
  const [bankname, setbankname] = useState("");
  const [bankbranchname, setbankbranchname] = useState("");
  const [ifsccode, setifsccode] = useState("");
  const [statecode, setstatecode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({
    branchname: "",
    contactperson: "",
    phoneno: "",
    email: "",
    gstno: "",
    address: "",
    zipcode: "",
    accountno: "",
    bankname: "",
    bankbranchname: "",
    ifsccode: "",
    country: "",
    state: "",
    city: ""
  });

  const [touched, setTouched] = useState({
    branchname: false,
    contactperson: false,
    phoneno: false,
    email: false,
    gstno: false,
    address: false,
    zipcode: false,
    accountno: false,
    bankname: false,
    bankbranchname: false,
    ifsccode: false,
    country: false,
    state: false,
    city: false
  });

  // Capitalization functions
  const capitalizeFirstLetter = (text) => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const capitalizeAllWords = (text) => {
    if (!text) return text;
    return text.split(' ').map(word => {
      if (word.length > 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word;
    }).join(' ');
  };

  const capitalizeEachWord = (text) => {
    if (!text) return text;
    return text.toLowerCase().split(' ').map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(BASE_URL + 'country/findAllCountry', { headers: authHeader() });
        setcountryOptions(response.data.map(country => ({ value: country.id, label: country.countryname })));
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch countries. Please try again.' });
      }
    };
    fetchCountries();
  }, []);

  // Enhanced validation function
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "branchname":
        if (!value.trim()) error = "Branch Name is required";
        else if (value.trim().length < 3) error = "Branch Name must be at least 3 characters";
        else if (value.trim().length > 100) error = "Branch Name must be less than 100 characters";
        break;

      case "contactperson":
        if (!value.trim()) error = "Contact Person is required";
        else if (value.trim().length < 3) error = "Contact Person must be at least 3 characters";
        else if (value.trim().length > 100) error = "Contact Person must be less than 100 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = "Contact Person should contain only letters and spaces";
        break;

      case "phoneno":
        if (!value.trim()) error = "Mobile No. is required";
        else if (!/^[6-9]\d{9}$/.test(value)) error = "Please enter a valid 10-digit mobile number starting with 6-9";
        break;

      case "email":
        if (!value.trim()) error = "Email ID is required";
        else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) error = "Please enter a valid email address";
        break;

      case "gstno":
        if (value.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/.test(value))
          error = "Please enter a valid GST number (Format: 22AAAAA0000A1Z5)";
        break;

      case "address":
        if (value.trim() && value.trim().length < 5) error = "Address must be at least 5 characters if provided";
        break;

      case "zipcode":
        if (value.trim() && !/^[1-9][0-9]{5}$/.test(value))
          error = "Please enter a valid 6-digit pincode";
        break;

      case "accountno":
        if (value.trim()) {
          if (!/^[0-9]+$/.test(value)) error = "Account number should contain only digits";
          else if (value.trim().length < 8) error = "Account number must be at least 8 digits";
          else if (value.trim().length > 18) error = "Account number must be less than 18 digits";
        }
        break;

      case "bankname":
        if (value.trim() && value.trim().length < 2) error = "Bank name must be at least 2 characters";
        break;

      case "bankbranchname":
        if (value.trim() && value.trim().length < 2) error = "Bank branch name must be at least 2 characters";
        break;

      case "ifsccode":
        if (value.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase()))
          error = "Please enter a valid IFSC code (Format: SBIN0123456)";
        break;

      case "country":
        if (!value) error = "Country is required";
        break;

      case "state":
        if (!value) error = "State is required";
        break;

      case "city":
        if (!value) error = "City is required";
        break;

      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply capitalization based on field
    switch (name) {
      case "branchname":
        processedValue = capitalizeAllWords(value);
        break;
      case "contactperson":
        processedValue = capitalizeAllWords(value);
        break;
      case "phoneno":
        processedValue = value.replace(/\D/g, '').slice(0, 10); // Only numbers, max 10 digits
        break;
      case "email":
        processedValue = value.toLowerCase();
        break;
      case "gstno":
        processedValue = value.toUpperCase().slice(0, 15); // Max 15 chars for GST
        break;
      case "address":
        processedValue = capitalizeAllWords(value);
        break;
      case "bankname":
        processedValue = capitalizeAllWords(value);
        break;
      case "bankbranchname":
        processedValue = capitalizeAllWords(value);
        break;
      case "ifsccode":
        processedValue = value.toUpperCase().slice(0, 11); // IFSC is 11 chars
        break;
      case "zipcode":
        processedValue = value.replace(/\D/g, '').slice(0, 6); // Only numbers, max 6 digits
        break;
      case "accountno":
        processedValue = value.replace(/\D/g, '').slice(0, 18); // Only numbers, max 18 digits
        break;
      default:
        processedValue = value;
        break;
    }

    // Update state based on field
    switch (name) {
      case "branchname": setbranchname(processedValue); break;
      case "contactperson": setcontactperson(processedValue); break;
      case "phoneno": setphoneno(processedValue); break;
      case "email": setemail(processedValue); break;
      case "gstno": setgstno(processedValue); break;
      case "address": setaddress(processedValue); break;
      case "bankname": setbankname(processedValue); break;
      case "bankbranchname": setbankbranchname(processedValue); break;
      case "ifsccode": setifsccode(processedValue); break;
      case "zipcode": setzipcode(processedValue); break;
      case "accountno": setaccountno(processedValue); break;
      default: break;
    }

    if (touched[name]) setErrors(prev => ({ ...prev, [name]: validateField(name, processedValue) }));
  };

  const handleCountryChange = async (selectedOption) => {
    setcountry(selectedOption);
    setstate(null);
    setcity(null);
    setstatecode("");
    setTouched(prev => ({ ...prev, country: true, state: true, city: true }));

    if (!selectedOption) {
      setErrors(prev => ({ ...prev, country: "Country is required" }));
      return;
    }

    setErrors(prev => ({ ...prev, country: "" }));

    try {
      const response = await axios.get(BASE_URL + `state/stateByCountry/${selectedOption.value}`, { headers: authHeader() });
      setstateOptions(response.data.map(state => ({ value: state.id, label: state.statename, code: state.statecode })));
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch states. Please try again.' });
    }
  };

  const handleStateChange = async (selectedOption) => {
    setstate(selectedOption);
    setcity(null);
    setstatecode(selectedOption?.code || "");
    setTouched(prev => ({ ...prev, state: true, city: true }));

    if (!selectedOption) {
      setErrors(prev => ({ ...prev, state: "State is required" }));
      return;
    }

    setErrors(prev => ({ ...prev, state: "" }));

    try {
      const response = await axios.get(BASE_URL + `city/cityByStateId/${selectedOption.value}`, { headers: authHeader() });
      setcityOptions(response.data.map(city => ({ value: city.id, label: city.cityname })));
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch cities. Please try again.' });
    }
  };

  const handleCityChange = (selectedOption) => {
    setcity(selectedOption);
    setTouched(prev => ({ ...prev, city: true }));

    if (!selectedOption) {
      setErrors(prev => ({ ...prev, city: "City is required" }));
    } else {
      setErrors(prev => ({ ...prev, city: "" }));
    }
  };

  const checkRequiredFields = () => {
    const requiredFields = [
      { name: 'branchname', value: branchname, label: 'Branch Name' },
      { name: 'contactperson', value: contactperson, label: 'Contact Person' },
      { name: 'phoneno', value: phoneno, label: 'Mobile No.' },
      { name: 'email', value: email, label: 'Email ID' },
      { name: 'country', value: country, label: 'Country' },
      { name: 'state', value: state, label: 'State' },
      { name: 'city', value: city, label: 'City' }
    ];

    const missingFields = requiredFields.filter(field => {
      if (field.name === 'country' || field.name === 'state' || field.name === 'city') {
        return !field.value;
      }
      return !field.value.trim();
    });

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Fields',
        text: `Please fill in: ${missingFields.map(f => f.label).join(', ')}`
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    // Validate all fields
    const newErrors = {
      branchname: validateField('branchname', branchname),
      contactperson: validateField('contactperson', contactperson),
      phoneno: validateField('phoneno', phoneno),
      email: validateField('email', email),
      gstno: validateField('gstno', gstno),
      address: validateField('address', address),
      zipcode: validateField('zipcode', zipcode),
      accountno: validateField('accountno', accountno),
      bankname: validateField('bankname', bankname),
      bankbranchname: validateField('bankbranchname', bankbranchname),
      ifsccode: validateField('ifsccode', ifsccode),
      country: validateField('country', country),
      state: validateField('state', state),
      city: validateField('city', city)
    };

    setErrors(newErrors);

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some(error => error !== "");

    if (!checkRequiredFields() || hasErrors) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please check all fields and fix the errors before submitting.'
      });
      return;
    }

    setIsLoading(true);

    const branchData = {
      country: country ? { id: country.value } : null,
      state: state ? { id: state.value } : null,
      city: city ? { id: city.value } : null,
      company: { id: 1 },
      branchname: branchname.trim(),
      phoneno: phoneno.trim(),
      gstno: gstno.trim() || null,
      email: email.toLowerCase().trim(),
      address: address.trim() || null,
      zipcode: zipcode.trim() || null,
      accountno: accountno.trim() || null,
      bankname: bankname.trim() || null,
      bankbranchname: bankbranchname.trim() || null,
      ifsccode: ifsccode.trim().toUpperCase() || null,
      contactperson: contactperson.trim(),
      statecode: statecode || null
    };

    try {
      await axios.post(BASE_URL + `branch/save`, branchData, { headers: authHeader() });
      Swal.fire({ icon: 'success', title: 'Success', text: 'Branch added successfully!' });
      navigate("/branchindex/1");
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.response?.data || "Failed to save branch"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <Card className="brand-card">
        <Card.Header className="brand-header">
          <h6>ADD NEW BRANCH</h6>
          <Link to="/branchindex/1">
            <button className="button-70" type="button">BACK</button>
          </Link>
        </Card.Header>

        <Card.Body className="brand-body">
          <Form onSubmit={handleSubmit}>

            {/* Basic Info */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Branch Name</Form.Label>
                  <Form.Control
                    className="form-control"
                    type="text"
                    name="branchname"
                    value={branchname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.branchname && !!errors.branchname}
                    placeholder="Enter Branch Name"
                  />
                  <Form.Control.Feedback type="invalid">{errors.branchname}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Contact Person</Form.Label>
                  <Form.Control
                    className="form-control"
                    type="text"
                    name="contactperson"
                    value={contactperson}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.contactperson && !!errors.contactperson}
                    placeholder="Enter Contact Person Name"
                  />
                  <Form.Control.Feedback type="invalid">{errors.contactperson}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Mobile No.</Form.Label>
                  <Form.Control
                    className="form-control"
                    type="tel"
                    name="phoneno"
                    value={phoneno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.phoneno && !!errors.phoneno}
                    placeholder="Enter 10-digit Mobile Number"
                    maxLength="10"
                  />
                  <Form.Control.Feedback type="invalid">{errors.phoneno}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Email ID</Form.Label>
                  <Form.Control
                    className="form-control"
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    placeholder="Enter Email Address"
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label">GST No.</Form.Label>
                  <Form.Control
                    className="form-control"
                    type="text"
                    name="gstno"
                    value={gstno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.gstno && !!errors.gstno}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength="15"
                  />
                  <Form.Control.Feedback type="invalid">{errors.gstno}</Form.Control.Feedback>
                  <Form.Text className="text-muted">Format: 22AAAAA0000A1Z5 (Optional)</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Address Details */}
            <Card className="brand-card mb-4">
              <Card.Header className="brand-header">
                <h6>ADDRESS DETAILS</h6>
              </Card.Header>
              <Card.Body className="brand-body">
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.address && !!errors.address}
                        placeholder="Enter address (Optional)"
                        className="custom-textarea"
                      />
                      <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label required">Country</Form.Label>
                      <Select
                        value={country}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        placeholder="Select country"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {touched.country && errors.country && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.country}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label required">State</Form.Label>
                      <Select
                        value={state}
                        onChange={handleStateChange}
                        options={stateOptions}
                        placeholder="Select state"
                        isDisabled={!country}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {touched.state && errors.state && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.state}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">State Code</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        value={statecode}
                        readOnly
                        placeholder="Auto-filled"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label required">City</Form.Label>
                      <Select
                        value={city}
                        onChange={handleCityChange}
                        options={cityOptions}
                        placeholder="Select city"
                        isDisabled={!state}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {touched.city && errors.city && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.city}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Zip Code</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="zipcode"
                        value={zipcode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.zipcode && !!errors.zipcode}
                        placeholder="Enter 6-digit pincode (Optional)"
                        maxLength="6"
                      />
                      <Form.Control.Feedback type="invalid">{errors.zipcode}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Bank Details */}
            <Card className="brand-card mb-4">
              <Card.Header className="brand-header">
                <h6>BANK DETAILS</h6>
              </Card.Header>
              <Card.Body className="brand-body">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Bank Name</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="bankname"
                        value={bankname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.bankname && !!errors.bankname}
                        placeholder="Enter bank name (Optional)"
                      />
                      <Form.Control.Feedback type="invalid">{errors.bankname}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Account No.</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="accountno"
                        value={accountno}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.accountno && !!errors.accountno}
                        placeholder="Enter account number (Optional)"
                        maxLength="18"
                      />
                      <Form.Control.Feedback type="invalid">{errors.accountno}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Branch Name</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="bankbranchname"
                        value={bankbranchname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.bankbranchname && !!errors.bankbranchname}
                        placeholder="Enter branch name (Optional)"
                      />
                      <Form.Control.Feedback type="invalid">{errors.bankbranchname}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">IFSC Code</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="ifsccode"
                        value={ifsccode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.ifsccode && !!errors.ifsccode}
                        placeholder="Enter IFSC code (Optional)"
                        maxLength="11"
                      />
                      <Form.Control.Feedback type="invalid">{errors.ifsccode}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Format: SBIN0123456 (Optional)</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mt-2">
              <button type="submit" className="button-70" disabled={isLoading}>
                {isLoading ? <Spinner animation="border" size="sm" /> : "SUBMIT"}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BranchCreate;